import React from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Controller } from "react-hook-form";
import { FormHelperText, Divider } from "@mui/material";
import { Checkbox, ListItemText } from "@mui/material";

// Custom Option to include checkbox in the dropdown and divider for single select
const CustomOption = (props: any) => {
  const { data, innerRef, innerProps, isSelected, isMulti, isLast } = props;

  return (
    <>
      <div ref={innerRef} {...innerProps} style={{ display: "flex", alignItems: "center" }}>
        {isMulti && <Checkbox checked={isSelected} />} {/* Only show checkbox for multi-select */}
        <ListItemText sx={{ px: 1 }} primary={data.label} />
      </div>
      {/* Divider is added only for single select */}
      {!isMulti && !isLast && <Divider />}
    </>
  );
};

const MultiSelect = (props: any) => {
  const { options, name, control, noError, multiple = true, disabled = false, onChange = () => { }, creatable = false, freesolo = false, onMenuOpen = () => { } } = props;

  // State to track the current input value for creatable select
  const [inputValue, setInputValue] = React.useState("");

  // Helper function to create a new option from input value
  const createOption = (label: string) => ({
    label,
    value: label.toLowerCase().replace(/\W/g, ''),
  });


  // Custom styles for the select dropdown
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: "99px",
      boxShadow: "inset 0 0 11px 2px #dfd9d9",
      padding: "5px",
      overflowX: "auto",
      display: "flex",
      flexWrap: "nowrap",
      fontFamily: "Poppins", // Apply font to select label
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      display: "flex",
      flexWrap: "nowrap",
      overflowX: "auto",
      scrollbarWidth: "none",
      fontFamily: "Poppins", // Apply font to selected value
      "::-webkit-scrollbar": {
        display: "none",
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      fontFamily: "Poppins", // Apply font to menu list
      position: "absolute",
      width: "100%",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      backgroundColor: "white",
      top: "100%", // Always position below the control
      bottom: "auto", // Override any bottom positioning
      transform: "none", // Remove any transforms that might affect positioning
      // Mobile-specific adjustments
      "@media (max-width: 768px)": {
        maxHeight: "200px",
        overflowY: "auto",
        top: "100%", // Ensure it's always below on mobile too
        bottom: "auto",
      },
    }),
    menuList: (provided: any) => ({
      ...provided,
      fontFamily: "Poppins, sans-serif !important",
      maxHeight: "200px",
      overflowY: "auto",
      // Better scrolling on mobile
      "-webkit-overflow-scrolling": "touch",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontFamily: "Poppins", // Apply font to each option
      backgroundColor: state.isSelected ? "#FF8960" : "white",
      color: state.isSelected ? "white" : "black",
      ":hover": {
        backgroundColor: "#FFD3B6",
        color: "black",
      },
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: "#FBEEED",
      fontFamily: "Poppins",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "#FF8960",
      fontFamily: "Poppins",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: "#FF8960",
      fontFamily: "Poppins",
      ":hover": {
        backgroundColor: "#FF8960",
        color: "white",
      },
    }),
  };

  const SelectComponent = creatable || freesolo ? CreatableSelect : Select;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <div style={{
            marginTop: "10px",
            width: "100%",
            position: "relative",
            // Ensure dropdown container doesn't overflow on mobile
            overflow: "visible"
          }}>
            <SelectComponent
              isMulti={multiple}
              options={options}
              value={field.value || (multiple ? [] : null)}
              onChange={(value) => {
                field.onChange(value);
                onChange(value);
              }}
              onInputChange={(value) => {
                setInputValue(value);
              }}
              inputValue={creatable ? inputValue : undefined}
              isDisabled={disabled}
              placeholder={creatable ? `Select or type` : `Select or search`}
              styles={customStyles}
              menuPlacement="bottom" // Force dropdown to always open downwards
              menuPosition="absolute" // Changed from fixed to absolute for better mobile positioning
              menuShouldScrollIntoView={false} // Prevents automatic scrolling that can cause positioning issues
              onMenuOpen={onMenuOpen}
              closeMenuOnSelect={!multiple}
              hideSelectedOptions={false}
              blurInputOnSelect={!multiple}
              formatCreateLabel={(inputValue) => inputValue}
              isClearable={creatable || freesolo ? (!multiple || freesolo) : false}
              allowCreateWhileLoading={freesolo}
              createOptionPosition={freesolo ? "first" : "last"}
              isValidNewOption={(inputValue) => {
                if (freesolo) {
                  return inputValue.trim().length > 0;
                }
                return true;
              }}
              components={{
                Option: (props) => {
                  const isLast = props.options[props.options.length - 1] === props.data;
                  return CustomOption({ ...props, isMulti: multiple, isLast });
                },
              }}
            />
          </div>
          {noError ? null : (
            <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: "14px" }}>{error ? error?.message : " "}</FormHelperText>
          )}
        </>
      )}
    />
  );
};

export default MultiSelect;
